import { ArrowLeftOutlined, MessageOutlined } from '@ant-design/icons'
import type { AssistantPlacementHistory } from '@stores/assistantPlacementHistory'
import { Alert } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useId, useRef } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import ConversationRows from './ConversationRows'
import { ERROR_MESSAGES } from './errorMessages'
import { useAssistantPlacement } from './placements/PlacementContext'
import { KW } from './ui'

interface Props {
  /** The placement session's own list (session.placementHistory). */
  history: AssistantPlacementHistory
  /** Back to the chat ("Back to chat", Escape): the panel puts the focus on its History button. */
  onBack: () => void
}

/** Used when a placement with a History of its own does not say what an empty one says. */
const DEFAULT_EMPTY_TEXT = 'No earlier conversations here'

/**
 * The History of a placement whose list is its own (historyScope 'placement', e.g. the Case
 * history pane): only this placement's conversations about its record, with the same rows as
 * the drawer's History (open, rename, delete) but no filter and no placement badge (every row
 * is one), compact for a narrow pane. The drawer's list, its filter and its pages are not
 * touched. Opening it puts the focus on its heading.
 */
const PlacementConversationList = ({ history, onBack }: Props) => {
  const session = useAssistantSession()
  const placement = useAssistantPlacement()
  const { items, total, hasMore, loaded, loading, loadingMore, failed } =
    history
  const headingId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
    // Shown again without being opened (the pane was closed on its History and opened again):
    // read anew, as opening it does (see AssistantSession.toggleHistory).
    if (!history.loading) {
      history.load()
    }
    // Once per mount: a list shown again is read again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emptyText = placement.historyEmptyText ?? DEFAULT_EMPTY_TEXT

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      role="region"
      aria-labelledby={headingId}
    >
      <div
        className="flex flex-shrink-0 items-center justify-between gap-2 px-3 py-[8px]"
        style={{ borderBottom: `1px solid ${KW.line}` }}
      >
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="m-0 min-w-0 truncate text-[13px] font-extrabold outline-none"
          style={{ color: KW.ink }}
        >
          Conversations
          {loaded && (
            <>
              {' '}
              <span style={{ color: KW.muted }}>
                ({Math.max(total, items.length)})
              </span>
            </>
          )}
        </h2>
        <button type="button" className="kw-history-back" onClick={onBack}>
          <ArrowLeftOutlined /> Back to chat
        </button>
      </div>

      {/* The chat (where errors normally show) is hidden behind the history. */}
      {session.errorCode === 'CONVERSATION_LOAD_FAILED' && (
        <Alert
          className="mx-3 mt-3 flex-shrink-0"
          type="warning"
          showIcon
          closable
          onClose={session.dismissError}
          message={ERROR_MESSAGES.CONVERSATION_LOAD_FAILED}
        />
      )}

      <div
        className="kw-scroll kw-history min-h-0 flex-1 overflow-y-auto px-1 pb-3"
        aria-busy={loading || undefined}
      >
        <ConversationRows items={items} showBadges={false} />

        {!loaded && loading && (
          <div
            role="status"
            className="px-3 py-6 text-center text-[13px]"
            style={{ color: KW.muted }}
          >
            Loading conversations…
          </div>
        )}

        {failed && !loading && !items.length && (
          <div
            role="alert"
            className="flex flex-col items-center gap-3 px-3 py-6 text-center text-[13px]"
            style={{ color: KW.muted }}
          >
            The conversations could not be loaded.
            <button
              type="button"
              className="kw-history-btn"
              onClick={history.load}
            >
              Try again
            </button>
          </div>
        )}

        {loaded && !failed && !items.length && (
          <div
            role="status"
            className="flex flex-col items-center gap-3 px-3 py-8 text-center"
          >
            <MessageOutlined style={{ fontSize: 24, color: KW.line2 }} />
            <span className="text-[13px] font-bold" style={{ color: KW.ink }}>
              {emptyText}
            </span>
          </div>
        )}

        {hasMore && items.length > 0 && (
          <div className="flex justify-center px-3 pt-3">
            <button
              type="button"
              className="kw-history-btn"
              disabled={loadingMore || loading}
              onClick={history.loadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(PlacementConversationList)
